import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  Target,
  CheckCircle2,
  Clock,
  AlertCircle,
  RefreshCw,
  Eye,
  UserPlus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessionPipelineTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("chroToken") || localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/chro-enhanced/succession-pipeline`, getAuthHeaders());
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching succession data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A265]" />
      </div>
    );
  }

  const { summary, candidates, criticalRolesCovered } = data;

  const regionFlags = {
    usa: '🇺🇸',
    canada: '🇨🇦',
    jamaica: '🇯🇲',
    barbados: '🇧🇧',
    trinidad_tobago: '🇹🇹',
    panama: '🇵🇦'
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1a3a3a]">Leadership Succession Pipeline</h2>
          <p className="text-sm text-muted-foreground">Identify and develop future leaders</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <UserPlus className="w-4 h-4 mr-2" />
            Identify Gaps
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-[#1a3a3a]" />
              <span className="text-xs font-medium text-muted-foreground">Critical Roles</span>
            </div>
            <p className="text-2xl font-bold text-[#1a3a3a]">{summary.criticalRolesIdentified}</p>
            <p className="text-xs text-muted-foreground">identified</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-medium text-muted-foreground">Candidates</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{summary.successionCandidates}</p>
            <p className="text-xs text-muted-foreground">in pipeline</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-emerald-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">Ready Now</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{summary.readyNow}</p>
            <p className="text-xs text-emerald-600">immediate successors</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">Ready 1-2 Yrs</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{summary.readyIn1To2Years}</p>
            <p className="text-xs text-blue-600">developing</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="text-xs font-medium text-amber-700">Needs Dev</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{summary.needsDevelopment}</p>
            <p className="text-xs text-amber-600">long-term potential</p>
          </CardContent>
        </Card>
      </div>

      {/* Coverage Progress */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-semibold">Critical Roles Coverage</p>
              <p className="text-sm text-muted-foreground">Percentage of critical roles with ready successors</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-600">{criticalRolesCovered}%</p>
              <p className="text-sm text-muted-foreground">{summary.readyNow} of {summary.criticalRolesIdentified} roles</p>
            </div>
          </div>
          <Progress value={criticalRolesCovered} className="h-4" />
        </CardContent>
      </Card>

      {/* Candidate Lists */}
      <div className="grid grid-cols-3 gap-4">
        {/* Ready Now */}
        <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              Ready Now ({candidates.readyNow?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {candidates.readyNow?.slice(0, 5).map((candidate, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-emerald-50 rounded-lg">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {candidate.name?.split(' ').map(n => n[0]).join('') || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{candidate.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{candidate.title}</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                    {candidate.briScore}
                  </Badge>
                </div>
              )) || <p className="text-sm text-muted-foreground">No candidates</p>}
            </div>
          </CardContent>
        </Card>

        {/* Ready in 1-2 Years */}
        <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              Ready in 1-2 Years ({candidates.readyIn1To2Years?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {candidates.readyIn1To2Years?.slice(0, 5).map((candidate, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {candidate.name?.split(' ').map(n => n[0]).join('') || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{candidate.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{candidate.title}</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                    {candidate.briScore}
                  </Badge>
                </div>
              )) || <p className="text-sm text-muted-foreground">No candidates</p>}
            </div>
          </CardContent>
        </Card>

        {/* Needs Development */}
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              Needs Development ({candidates.needsDevelopment?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {candidates.needsDevelopment?.slice(0, 5).map((candidate, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-amber-50 rounded-lg">
                  <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {candidate.name?.split(' ').map(n => n[0]).join('') || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{candidate.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{candidate.title}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                    {candidate.briScore}
                  </Badge>
                </div>
              )) || <p className="text-sm text-muted-foreground">No candidates</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card className="border-0 shadow-sm bg-slate-50">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold">Succession Planning Actions</p>
              <p className="text-sm text-muted-foreground">Recommended next steps for leadership development</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                View Succession Plans
              </Button>
              <Button className="bg-[#7C3AED] hover:bg-[#7C3AED]/90" size="sm">
                <UserPlus className="w-4 h-4 mr-2" />
                Identify Gaps
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
